import React, { useState, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

/* ─── constants ─────────────────────────────────────────────── */
const PAGE_LIMIT = 20;

const STATUS_CONFIG = {
  Answered: { color: "success", icon: <PhoneIcon sx={{ fontSize: 12 }} /> },
  "Not Answered": {
    color: "error",
    icon: <PhoneDisabledIcon sx={{ fontSize: 12 }} />,
  },
  Busy: { color: "warning", icon: null },
  Failed: { color: "error", icon: null },
};

const COLUMNS = [
  { label: "Customer ID", field: (r, p) => r.customerId, width: 160 },
  {
    label: "Campaign ID",
    field: (r, p) => p.campaign_id,
    width: 110,
    mono: true,
  },
  {
    label: "Campaign Name",
    field: (r, p) => p.campaign_name,
    width: 200,
    truncate: true,
  },
  { label: "Mobile", field: (r, p) => p.mobile, width: 120, mono: true },
  { label: "CLI", field: (r, p) => p.cli, width: 120, mono: true },
  {
    label: "Answer Status",
    field: (r, p) => p.answer_status,
    width: 130,
    chip: true,
  },
  { label: "Duration", field: (r, p) => fmtDuration(r.durationSec), width: 90 },
  { label: "Billing (min)", field: (r, p) => p.billing_duration, width: 100 },
  { label: "Credits", field: (r, p) => r.creditsDebited, width: 80 },
  {
    label: "Hangup Cause",
    field: (r, p) => p.hangup_cause,
    width: 180,
    truncate: true,
  },
  { label: "Retry Count", field: (r, p) => p.retry_count, width: 90 },
  {
    label: "Answer Time",
    field: (r, p) => fmtDateTime(p.answer_time),
    width: 150,
  },
  {
    label: "Start Time",
    field: (r, p) => fmtDateTime(p.start_time),
    width: 150,
  },
  { label: "End Time", field: (r, p) => fmtDateTime(p.end_time), width: 150 },
  {
    label: "Created On",
    field: (r, p) => fmtDateTime(r.createdOn),
    width: 150,
  },
];

/* ─── helpers ───────────────────────────────────────────────── */
const fmtDuration = (sec) => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60),
    s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildQuery = (page, filters) => {
  const params = new URLSearchParams({ page, limit: PAGE_LIMIT });
  if (filters.name) params.set("name", filters.name);
  if (filters.campaignId) params.set("campaignId", filters.campaignId);
  return `/voice/get-voice-history?${params.toString()}`;
};

/* ─── sub-components ────────────────────────────────────────── */
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: "default", icon: null };
  return (
    <Chip
      label={status || "Unknown"}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

const CellValue = ({ col, row, payload }) => {
  const val = col.field(row, payload);
  const display = val ?? "—";

  if (col.chip) return <StatusChip status={val} />;
  if (col.truncate)
    return (
      <Tooltip title={display} placement="top">
        <Typography
          variant="caption"
          noWrap
          sx={{ display: "block", maxWidth: col.width - 20 }}
        >
          {display}
        </Typography>
      </Tooltip>
    );
  return (
    <Typography
      variant="caption"
      sx={{ fontFamily: col.mono ? "monospace" : "inherit" }}
    >
      {String(display)}
    </Typography>
  );
};

/* ─── export helper ─────────────────────────────────────────── */
const flattenRow = (r) => {
  const p = r.payload ?? {};
  return {
    "Customer ID": r.customerId,
    "Campaign ID": p.campaign_id,
    "Campaign Name": p.campaign_name,
    Mobile: p.mobile,
    CLI: p.cli,
    "Answer Status": p.answer_status,
    "Duration (sec)": r.durationSec,
    "Billing (min)": p.billing_duration,
    Credits: r.creditsDebited,
    DTMF: p.dtmf,
    "Hangup Cause": p.hangup_cause,
    "Retry Count": p.retry_count,
    "Agent Number": p.agent_number,
    "Recording File": p.recording_file,
    "Answer Time": p.answer_time,
    "Start Time": p.start_time,
    "End Time": p.end_time,
    "Created On": r.createdOn,
  };
};

/* ─── main component ────────────────────────────────────────── */
const VoiceTransactonHistory = () => {
  // filter state (committed on search)
  const [committed, setCommitted] = useState({ name: "", campaignId: "" });
  // live input state
  const [inputs, setInputs] = useState({ name: "", campaignId: "" });
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useApiEp(buildQuery(page, committed));

  const rows = response?.data ?? [];
  const totalPages = response?.totalPages ?? 0;
  const totalItems = response?.totalItems ?? 0;

  /* apply filters */
  const handleSearch = () => {
    setPage(1);
    setCommitted({ ...inputs });
  };

  /* clear all */
  const handleClear = () => {
    const empty = { name: "", campaignId: "" };
    setInputs(empty);
    setCommitted(empty);
    setPage(1);
  };

  const anyFilter = inputs.name || inputs.campaignId;

  /* export */
  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportErr("");
    try {
      const params = new URLSearchParams();
      if (committed.name) params.set("name", committed.name);
      if (committed.campaignId) params.set("campaignId", committed.campaignId);
      const url = `${API_ENDPOINT}/voice/get-full-voice-history?${params.toString()}`;
      const { data } = await axios.get(url, getHeader());
      const flat = data.map(flattenRow);
      const ws = XLSX.utils.json_to_sheet(flat);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Voice History");
      const ts = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `voice-history-${ts}.xlsx`);
    } catch (e) {
      setExportErr("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [committed]);

  /* ── render ── */
  return (
    <Box sx={{ p: 3 }}>
      {/* Header row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Voice Transaction History
          </Typography>
          {totalItems > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totalItems.toLocaleString()} records
              {(committed.name || committed.campaignId) && " (filtered)"}
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={exporting}
          sx={{ whiteSpace: "nowrap" }}
        >
          {exporting ? "Exporting…" : "Export Excel"}
        </Button>
      </Stack>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
        >
          <TextField
            size="small"
            label="Customer ID"
            placeholder="Search customer…"
            value={inputs.name}
            onChange={(e) => setInputs((s) => ({ ...s, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            size="small"
            label="Campaign ID"
            placeholder="e.g. 940725"
            value={inputs.campaignId}
            onChange={(e) =>
              setInputs((s) => ({ ...s, campaignId: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 180 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ px: 3 }}
          >
            Search
          </Button>
          {anyFilter && (
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {exportErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportErr}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert severity="error">
          {error?.message ?? "Failed to load voice history."}
        </Alert>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.label}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        whiteSpace: "nowrap",
                        minWidth: col.width,
                        bgcolor: "grey.50",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      align="center"
                      sx={{ py: 6 }}
                    >
                      <Typography color="text.secondary" variant="body2">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const p = row.payload ?? {};
                    return (
                      <TableRow key={row._id} hover>
                        {COLUMNS.map((col) => (
                          <TableCell key={col.label} sx={{ py: 0.8 }}>
                            <CellValue col={col} row={row} payload={p} />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default VoiceTransactonHistory;
